import { FC, lazy, LazyExoticComponent } from "react";
import FullLayout from 'layouts/full';
import FullLayoutWithFilters from 'layouts/fullWithFilters';
import CommonLayout from './layouts/common';
import retry from "routes/retry";
import { Roles } from "store/reducers/session/types";

export type AppRoute = {
  path: string;
  component: LazyExoticComponent<FC<any>>;
  layout: FC<any> | null;
  title?: string;
  exact?: boolean;
  isPrivate?: boolean;
  tabNav?: boolean;
  allowedRoles?: Roles[];
};

const routes: Array<AppRoute> = [
  {
    path: "/",
    component: lazy(() => retry(() => import("pages/dashboard"))),
    layout: FullLayout,
    title: "Dashboard",
    exact: true,
    isPrivate: true,
    tabNav: true
  },
  {
    path: "/app/occupancy-rate",
    component: lazy(() => retry(() => import("pages/occupancy-rate"))),
    layout: FullLayout,
    title: "Occupancy Rate",
    exact: true,
    isPrivate: true,
    tabNav: true
  },
  {
    path: "/app/occupancy-insights",
    component: lazy(() => retry(() => import("pages/occupancy-insights"))),
    layout: FullLayout,
    title: "Occupancy Insights",
    exact: true,
    isPrivate: true,
    tabNav: true,
    allowedRoles: [Roles.SuperAdmin],
  },
  {
    path: "/login",
    component: lazy(() => retry(() => import("pages/login"))),
    layout: CommonLayout,
    title: "Login",
    exact: true,
  },
  {
    path: "/app/requests/:type?",
    component: lazy(() => retry(() => import("pages/requests"))),
    layout: FullLayoutWithFilters,
    title: "Requests",
    exact: true,
    isPrivate: true,
    tabNav: true
  },
  {
    path: "/app/requests-mass-actions",
    component: lazy(() => retry(() => import("pages/requests-mass-actions"))),
    layout: FullLayout,
    title: "Requests Mass Actions",
    exact: true,
    isPrivate: true,
    allowedRoles: [Roles.Admin, Roles.SuperAdmin],
  },
  {
    path: "/app/reservations-mass-actions",
    component: lazy(() => retry(() => import("pages/reservations-mass-actions"))),
    layout: FullLayout,
    title: "Reservations Mass Actions",
    exact: true,
    isPrivate: true,
    allowedRoles: [Roles.Admin, Roles.SuperAdmin],
  },
  {
    path: "/app/reservations/:type?",
    component: lazy(() => retry(() => import("pages/reservations"))),
    layout: FullLayoutWithFilters,
    exact: true,
    title: "Reservations",
    isPrivate: true,
    tabNav: true,
  },
  {
    path: "/app/reservations/lodging/:id",
    component: lazy(() => retry(() => import("pages/lodging"))),
    layout: FullLayout,
    exact: true,
    title: "Lodging",
    isPrivate: true
  },
  {
    path: "/app/lodgings/:type?",
    component: lazy(() => retry(() => import("pages/lodgings"))),
    layout: FullLayoutWithFilters,
    exact: true,
    title: 'Lodgings',
    isPrivate: true,
    tabNav: true
  },
  {
    path: "/app/users",
    component: lazy(() => retry(() => import("pages/users-management"))),
    layout: FullLayout,
    exact: true,
    title: 'Users Management',
    isPrivate: true,
    allowedRoles: [Roles.SuperAdmin],
    tabNav: true
  },
  {
    path: "/app/users-activity",
    component: lazy(() => retry(() => import("pages/users-activity"))),
    layout: FullLayout,
    exact: true,
    title: 'Users Activity',
    isPrivate: true,
    allowedRoles: [Roles.SuperAdmin],
    tabNav: true
  },
  {
    path: "/app/lodging-policy",
    component: lazy(() => retry(() => import("pages/lodging-policy"))),
    layout: FullLayout,
    exact: true,
    title: 'Manage Lodging Policies',
    isPrivate: true,
    allowedRoles: [Roles.SuperAdmin],
    tabNav: true
  },
  {
    path: "/app/external-calls",
    component: lazy(() => retry(() => import("pages/external-calls-logs"))),
    layout: FullLayout,
    exact: true,
    title: 'External API Calls',
    isPrivate: true,
    allowedRoles: [Roles.SuperAdmin],
    tabNav: true
  },
  {
    path: "/app/automated-reports",
    component: lazy(() => retry(() => import("pages/automated-reports"))),
    layout: FullLayout,
    exact: true,
    title: 'Automated Reporting',
    isPrivate: true,
    allowedRoles: [Roles.SuperAdmin],
    tabNav: true
  },
  {
    path: "/app/corporate-clients",
    component: lazy(() => retry(() => import("pages/corporate-clients"))),
    layout: FullLayout,
    exact: true,
    title: 'Clients',
    isPrivate: true,
    allowedRoles: [Roles.SuperAdmin],
    tabNav: true
  },
  {
    path: "/app/reservation-providers",
    component: lazy(() => retry(() => import("pages/reservation-providers"))),
    layout: FullLayout,
    exact: true,
    title: 'Reservation Providers',
    isPrivate: true,
    allowedRoles: [Roles.SuperAdmin],
    tabNav: true
  },
  {
    path: "/app/merge-rooms",
    component: lazy(() => retry(() => import("pages/merge-rooms"))),
    layout: FullLayout,
    exact: true,
    title: 'Merge Lodging Options',
    isPrivate: true,
    allowedRoles: [Roles.SuperAdmin],
    tabNav: true
  },
  {
    path: "/app/export-jobs",
    component: lazy(() => retry(() => import("pages/export-jobs"))),
    layout: FullLayout,
    exact: true,
    title: 'Export Status',
    isPrivate: true,
    allowedRoles: [Roles.SuperAdmin],
    tabNav: true
  },
  {
    path: "/app/guests",
    component: lazy(() => retry(() => import("pages/guests"))),
    layout: FullLayoutWithFilters,
    exact: true,
    title: 'Guests',
    isPrivate: true,
    tabNav: false,
  },
  {
    path: "/terms-and-conditions",
    component: lazy(() => retry(() => import("pages/terms-of-use/termsAndConditions"))),
    layout: FullLayout,
    title: "Terms and Conditions",
  },
  {
    path: "/privacy",
    component: lazy(() => retry(() => import("pages/terms-of-use/privacy"))),
    layout: FullLayout,
    title: "Privacy",
  },
  {
    path: "/app/booking/:id?",
    component: lazy(() => retry(() => import("pages/booking"))),
    layout: FullLayout,
    title: "Booking",
    exact: true,
    isPrivate: true,
    tabNav: true,
  },
  {
    path: "/app/lodgings-and-reservations",
    component: lazy(() => retry(() => import("pages/booking"))),
    layout: FullLayout,
    exact: true,
    title: 'Lodgings and Reservations',
    isPrivate: true
  },
  {
    path: "/app/occupancy-rate/report/:id",
    component: lazy(() => retry(() => import("pages/occupancy-rate"))),
    layout: FullLayout,
    title: "Occupancy Rate",
    exact: true,
    isPrivate: true,
    tabNav: true
  },
  {
    path: "/app/requests/report/:id",
    component: lazy(() => retry(() => import("pages/requests"))),
    layout: FullLayoutWithFilters,
    title: "Requests",
    exact: true,
    isPrivate: true,
    tabNav: true
  },
  {
    path: "/app/reservations/report/:id",
    component: lazy(() => retry(() => import("pages/reservations"))),
    layout: FullLayoutWithFilters,
    title: "Reservations",
    exact: true,
    isPrivate: true,
    tabNav: true
  },
  {
    path: "/app/lodgings/report/:id",
    component: lazy(() => retry(() => import("pages/lodgings"))),
    layout: FullLayoutWithFilters,
    exact: true,
    title: 'Lodgings',
    isPrivate: true
  },
];

export default routes;